/**
 * Browser-safe utility functions for JWT operations
 * These functions work in the browser without Node.js dependencies
 */
/**
 * Decode JWT without verification (browser-safe)
 */
export function decodeJWTSafe(token) {
    try {
        const base64Url = token.split('.')[1];
        if (!base64Url)
            return null;
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64)
            .split('')
            .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join(''));
        const payload = JSON.parse(jsonPayload);
        return typeof payload === 'object' && payload !== null && !Array.isArray(payload)
            ? payload
            : null;
    }
    catch {
        return null;
    }
}
/**
 * Check if JWT has expired (browser-safe)
 */
export function isJWTExpiredSafe(token) {
    try {
        const payload = decodeJWTSafe(token);
        if (!payload || typeof payload.exp !== 'number' || !Number.isFinite(payload.exp))
            return true;
        const currentTime = Math.floor(Date.now() / 1000);
        return payload.exp < currentTime;
    }
    catch {
        return true;
    }
}
/**
 * Extract permission from JWT (browser-safe)
 */
export function hasPermissionSafe(token, permission) {
    try {
        const payload = decodeJWTSafe(token);
        if (!payload)
            return false;
        // Check custom claims first
        const permissions = payload.permissions || payload['https://stytch.com/permissions'] || [];
        if (Array.isArray(permissions)) {
            return permissions.includes(permission);
        }
        return false;
    }
    catch {
        return false;
    }
}
/**
 * Extract role from JWT (browser-safe)
 */
export function hasRoleSafe(token, role) {
    try {
        const payload = decodeJWTSafe(token);
        if (!payload)
            return false;
        // Check custom claims first
        const userRole = payload.role || payload['https://stytch.com/role'];
        if (userRole === role)
            return true;
        // Check roles array
        const roles = payload.roles || payload['https://stytch.com/roles'] || [];
        if (Array.isArray(roles)) {
            return roles.includes(role);
        }
        return false;
    }
    catch {
        return false;
    }
}
/**
 * Get Stytch session cookies from browser
 */
export function getStytchCookies() {
    if (typeof window === 'undefined') {
        return {};
    }
    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
        const [key, value] = cookie.trim().split('=');
        if (key && value) {
            acc[key] = value;
        }
        return acc;
    }, {});
    return {
        session_jwt: cookies.stytch_session_jwt,
        session_token: cookies.stytch_session,
    };
}
/**
 * Check if Stytch session cookies exist
 */
export function hasStytchSession() {
    const cookies = getStytchCookies();
    return !!(cookies.session_jwt || cookies.session_token);
}
/**
 * Check if there's an intermediate session token in sessionStorage
 * This indicates the user is in the middle of a discovery flow
 */
export function hasIntermediateSession() {
    if (typeof window === 'undefined') {
        return false;
    }
    const intermediateToken = sessionStorage.getItem('intermediateSessionToken') ?? sessionStorage.getItem('intermediate_session_token');
    return !!intermediateToken;
}
/**
 * Get intermediate session token from sessionStorage
 */
export function getIntermediateSessionToken() {
    if (typeof window === 'undefined') {
        return null;
    }
    return sessionStorage.getItem('intermediateSessionToken') ?? sessionStorage.getItem('intermediate_session_token');
}
/**
 * Clear intermediate session token and all auth-related items from sessionStorage
 * This ensures a clean slate when switching accounts/workspaces
 */
export function clearIntermediateSession() {
    if (typeof window === 'undefined') {
        return;
    }
    // Core intermediate session items
    sessionStorage.removeItem('intermediateSessionToken');
    sessionStorage.removeItem('intermediate_session_token');
    sessionStorage.removeItem('discoveredOrganizations');
    // MFA-related items
    sessionStorage.removeItem('mfa_verification_required');
    sessionStorage.removeItem('mfa_required_data');
    sessionStorage.removeItem('mfa_redirecting');
    // Auth flow tracking
    sessionStorage.removeItem('auth_method');
    // Clear per-session analytics guard keys so sign-in re-fires AND identify/alias
    // re-run for the next user (ZANE-7321: `analytics_identified_*` joins the
    // existing `analytics_signed_in_*`).
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.startsWith('analytics_signed_in_') || key?.startsWith('analytics_identified_')) {
            keysToRemove.push(key);
        }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
}
/**
 * Check Stytch cookies and log their presence
 */
export function checkStytchCookies() {
    if (typeof window === 'undefined')
        return;
    const cookies = getStytchCookies();
    console.log('🍪 [Stytch Cookies] Checking authentication cookies...');
    console.log('  stytch_session_jwt:', cookies.session_jwt ? '✅ Set' : '❌ Not set');
    console.log('  stytch_session:', cookies.session_token ? '✅ Set' : '❌ Not set');
    if (cookies.session_jwt || cookies.session_token) {
        console.log('✅ [Stytch Cookies] Authentication cookies are present!');
        // Decode and show basic info from JWT if available
        if (cookies.session_jwt) {
            try {
                const payload = decodeJWTSafe(cookies.session_jwt);
                if (payload) {
                    console.log('🔍 [Stytch Cookies] JWT contains:');
                    console.log('  - Member ID:', payload.sub || 'Not found');
                    console.log('  - Email:', payload.email || 'Not found');
                    console.log('  - Organization:', payload.organization_id || 'Not found');
                    console.log('  - Expires:', typeof payload.exp === 'number' ? new Date(payload.exp * 1000).toISOString() : 'Not found');
                }
            }
            catch {
                console.log('⚠️ [Stytch Cookies] Could not decode JWT payload');
            }
        }
    }
    else {
        console.log('❌ [Stytch Cookies] No authentication cookies found');
        console.log('💡 [Stytch Cookies] This might indicate an authentication issue');
    }
}
